import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import productService from '../../services/productService';
import './FeaturedProductsPage.css';

function FeaturedProductsPage() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [maxFeatured, setMaxFeatured] = useState(8);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [featuredRes, productsRes] = await Promise.all([
        productService.getFeaturedProducts(),
        productService.getProducts({ status: 'active', size: 100 }),
      ]);

      const featuredData = featuredRes.data;
      setFeaturedProducts(featuredData.products || []);
      setMaxFeatured(featuredData.maxFeatured || 8);

      // Get all active products that are not already featured
      const allProducts = productsRes.data.content || [];
      const featuredIds = new Set((featuredData.products || []).map((p) => p.id));
      setAvailableProducts(allProducts.filter((p) => !featuredIds.has(p.id)));
    } catch (err) {
      toast.error('Failed to load featured products');
      console.error('Error loading data:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddToFeatured = async (product) => {
    if (featuredProducts.length >= maxFeatured) {
      toast.warning(`Maximum ${maxFeatured} featured products allowed`);
      return;
    }

    setIsSaving(true);
    try {
      await productService.addToFeatured(product.id);
      setFeaturedProducts([...featuredProducts, product]);
      setAvailableProducts(availableProducts.filter((p) => p.id !== product.id));
      toast.success(`"${product.name}" added to featured`);
    } catch (err) {
      toast.error('Failed to add product to featured');
      console.error('Error adding to featured:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveFromFeatured = async (product) => {
    setIsSaving(true);
    try {
      await productService.removeFromFeatured(product.id);
      setFeaturedProducts(featuredProducts.filter((p) => p.id !== product.id));
      setAvailableProducts([...availableProducts, product]);
      toast.success(`"${product.name}" removed from featured`);
    } catch (err) {
      toast.error('Failed to remove product from featured');
      console.error('Error removing from featured:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newFeatured = [...featuredProducts];
    const draggedItem = newFeatured[draggedIndex];
    newFeatured.splice(draggedIndex, 1);
    newFeatured.splice(index, 0, draggedItem);
    setFeaturedProducts(newFeatured);
    setDraggedIndex(index);
  };

  const handleDragEnd = async () => {
    if (draggedIndex === null) return;

    setDraggedIndex(null);
    setIsSaving(true);

    try {
      const productIds = featuredProducts.map((p) => p.id);
      await productService.reorderFeatured(productIds);
      toast.success('Featured products reordered');
    } catch (err) {
      toast.error('Failed to save new order');
      console.error('Error reordering:', err);
      loadData(); // Reload original order on error
    } finally {
      setIsSaving(false);
    }
  };

  const moveProduct = async (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= featuredProducts.length) return;

    const newFeatured = [...featuredProducts];
    const temp = newFeatured[index];
    newFeatured[index] = newFeatured[newIndex];
    newFeatured[newIndex] = temp;
    setFeaturedProducts(newFeatured);

    setIsSaving(true);
    try {
      const productIds = newFeatured.map((p) => p.id);
      await productService.reorderFeatured(productIds);
    } catch (err) {
      toast.error('Failed to save new order');
      loadData();
    } finally {
      setIsSaving(false);
    }
  };

  const filteredAvailable = availableProducts.filter(
    (p) =>
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.sku.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(price);
  };

  if (isLoading) {
    return (
      <div className="featured-products-page">
        <div className="page-loading">
          <div className="loading-spinner"></div>
          <span>Loading featured products...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="featured-products-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Featured Products</h1>
          <p>Select and arrange products to display on your homepage.</p>
        </div>
        <div className="page-header-actions">
          <Link to="/products" className="btn btn-secondary">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
            </svg>
            All Products
          </Link>
        </div>
      </div>

      <div className="featured-layout">
        {/* Featured Products Section */}
        <div className="featured-section">
          <div className="section-header">
            <h2>Featured Products</h2>
            <span className="featured-count">
              {featuredProducts.length} / {maxFeatured}
            </span>
          </div>
          <p className="section-description">
            Drag and drop to reorder. These products will appear on your homepage.
          </p>

          {featuredProducts.length === 0 ? (
            <div className="empty-featured">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
              </svg>
              <p>No featured products yet</p>
              <span>Add products from the list below</span>
            </div>
          ) : (
            <div className="featured-list">
              {featuredProducts.map((product, index) => (
                <div
                  key={product.id}
                  className={`featured-item ${draggedIndex === index ? 'dragging' : ''}`}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                >
                  <div className="drag-handle">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                    </svg>
                  </div>
                  <span className="position-badge">{index + 1}</span>
                  <div className="product-image">
                    {product.images?.[0]?.url ? (
                      <img src={product.images[0].url} alt={product.name} />
                    ) : (
                      <div className="no-image">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="product-info">
                    <h4>{product.name}</h4>
                    <div className="product-meta">
                      <span className="sku">{product.sku}</span>
                      <span className="price">{formatPrice(product.price)}</span>
                    </div>
                  </div>
                  <div className="item-actions">
                    <button
                      className="btn-icon"
                      onClick={() => moveProduct(index, -1)}
                      disabled={index === 0 || isSaving}
                      title="Move up"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                      </svg>
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => moveProduct(index, 1)}
                      disabled={index === featuredProducts.length - 1 || isSaving}
                      title="Move down"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                      </svg>
                    </button>
                    <button
                      className="btn-icon danger"
                      onClick={() => handleRemoveFromFeatured(product)}
                      disabled={isSaving}
                      title="Remove from featured"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Available Products Section */}
        <div className="available-section">
          <div className="section-header">
            <h2>Available Products</h2>
            <span className="available-count">{availableProducts.length} products</span>
          </div>

          <div className="search-box">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="available-list">
            {filteredAvailable.length === 0 ? (
              <div className="empty-available">
                <p>{searchTerm ? 'No products match your search' : 'All active products are featured'}</p>
              </div>
            ) : (
              filteredAvailable.map((product) => (
                <div key={product.id} className="available-item">
                  <div className="product-image">
                    {product.images?.[0]?.url ? (
                      <img src={product.images[0].url} alt={product.name} />
                    ) : (
                      <div className="no-image">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="product-info">
                    <h4>{product.name}</h4>
                    <div className="product-meta">
                      <span className="sku">{product.sku}</span>
                      <span className="price">{formatPrice(product.price)}</span>
                    </div>
                  </div>
                  <button
                    className="btn btn-add"
                    onClick={() => handleAddToFeatured(product)}
                    disabled={featuredProducts.length >= maxFeatured || isSaving}
                    title="Add to featured"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    Add
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FeaturedProductsPage;
